/**
 * Campaign Results Screen - Screen 5
 *
 * Analytics dashboard showing campaign results and performance metrics.
 *
 * Design Pattern: Analytics dashboard with visualizations
 * - Metric Cards: Final results (Goal, Sourced, Contacted, Scheduled)
 * - Conversion Funnel: Visual representation of candidate flow
 * - Channel Breakdown: Effectiveness by communication channel
 * - Timeline Chart: Progress over time
 * - Scheduled Interviews: List of scheduled candidates
 *
 * Route: /campaigns/autonomous/:session_id/results
 */

import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Download,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Target,
  TrendingUp,
  Users,
  Copy,
  BarChart3,
  AlertTriangle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function CampaignResults({ previewMode = false }) {
  const { session_id } = useParams();
  const navigate = useNavigate();

  // Campaign state
  const [campaignStatus, setCampaignStatus] = useState('unknown');
  const [goalId, setGoalId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Analytics data
  const [analytics, setAnalytics] = useState({
    progress: {
      sourced_count: 0,
      evaluated_count: 0,
      contacted_count: 0,
      scheduled_count: 0
    },
    progress_percentage: 0,
    conversion_funnel: {
      sourced: 0,
      evaluated: 0,
      qualified: 0,
      contacted: 0,
      interested: 0,
      scheduled: 0
    },
    channel_metrics: {
      email: { sent: 0, response_rate: 0 },
      whatsapp: { sent: 0, response_rate: 0 },
      sms: { sent: 0, response_rate: 0 },
      call: { initiated: 0, completion_rate: 0 }
    },
    goal_achievement: {
      goal: 0,
      achieved: 0,
      percentage: 0
    },
    phase_failures: [],
    escalations: []
  });

  // Scheduled interviews
  const [scheduledInterviews, setScheduledInterviews] = useState([]);

  // Preview mode: Show sample data
  const isPreview = previewMode || !session_id;

  // Sample data for preview
  const sampleAnalytics = {
    progress: {
      sourced_count: 52,
      evaluated_count: 18,
      contacted_count: 12,
      scheduled_count: 10
    },
    progress_percentage: 100,
    conversion_funnel: {
      sourced: 52,
      evaluated: 18,
      qualified: 15,
      contacted: 12,
      interested: 10,
      scheduled: 10
    },
    channel_metrics: {
      email: { sent: 8, response_rate: 75 },
      whatsapp: { sent: 3, response_rate: 67 },
      sms: { sent: 0, response_rate: 0 },
      call: { initiated: 1, completion_rate: 100 }
    },
    goal_achievement: {
      goal: 10,
      achieved: 10,
      percentage: 100
    }
  };

  // Load analytics data
  useEffect(() => {
    if (isPreview) {
      setAnalytics(sampleAnalytics);
      setScheduledInterviews([]);
      setLoading(false);
      return;
    }

    const loadAnalytics = async () => {
      try {
        setLoadError(null);
        let resolvedGoalId = localStorage.getItem(`goal_id_${session_id}`);
        if (!resolvedGoalId) {
          const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/session/${session_id}/goal`);
          if (res.ok) {
            const data = await res.json();
            resolvedGoalId = data.goal_id;
            if (resolvedGoalId) {
              localStorage.setItem(`goal_id_${session_id}`, resolvedGoalId);
            }
          }
        }
        if (!resolvedGoalId) {
          console.warn('No goal_id found for session');
          setLoadError('No goal_id found for this session yet.');
          setScheduledInterviews([]);
          setLoading(false);
          return;
        }

        setGoalId(resolvedGoalId);

        // Fetch analytics
        const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${resolvedGoalId}/analytics`);
        if (!response.ok) {
          throw new Error('Failed to fetch analytics');
        }

        const data = await response.json();
        // Normalize so channel_metrics and other nested fields always exist
        setAnalytics({
          progress: data.progress || {},
          progress_percentage: data.progress_percentage ?? 0,
          conversion_funnel: data.conversion_funnel || {},
          channel_metrics: data.channel_metrics || {
            email: { sent: 0, response_rate: 0 },
            whatsapp: { sent: 0, response_rate: 0 },
            sms: { sent: 0, response_rate: 0 },
            call: { initiated: 0, completion_rate: 0 }
          },
          goal_achievement: data.goal_achievement || { goal: 0, achieved: 0, percentage: 0 },
          phase_failures: data.phase_failures || [],
          escalations: data.escalations || []
        });

        setScheduledInterviews(data.scheduled_interviews || []);

      } catch (error) {
        console.error('Error loading analytics:', error);
        setLoadError('Unable to load campaign analytics right now.');
        setScheduledInterviews([]);
      } finally {
        setLoading(false);
      }
    };

    if (session_id && !isPreview) {
      loadAnalytics();
    }
  }, [session_id, isPreview]);

  // Live updates: poll analytics every 20s when we have goalId
  useEffect(() => {
    if (isPreview || !goalId) return;

    const fetchLatest = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${goalId}/analytics`);
        if (!res.ok) return;
        const data = await res.json();
        setAnalytics({
          progress: data.progress || {},
          progress_percentage: data.progress_percentage ?? 0,
          conversion_funnel: data.conversion_funnel || {},
          channel_metrics: data.channel_metrics || {
            email: { sent: 0, response_rate: 0 },
            whatsapp: { sent: 0, response_rate: 0 },
            sms: { sent: 0, response_rate: 0 },
            call: { initiated: 0, completion_rate: 0 }
          },
          goal_achievement: data.goal_achievement || { goal: 0, achieved: 0, percentage: 0 },
          phase_failures: data.phase_failures || [],
          escalations: data.escalations || [],
          scheduled_interviews: data.scheduled_interviews || []
        });
        setScheduledInterviews(data.scheduled_interviews || []);
      } catch {
        // Ignore polling errors
      }
    };

    const interval = setInterval(fetchLatest, 20000);
    return () => clearInterval(interval);
  }, [goalId, isPreview]);

  // Calculate channel totals for breakdown (safe when channel_metrics is missing)
  const getChannelTotal = () => {
    const cm = analytics.channel_metrics || {};
    const email = cm.email || {};
    const whatsapp = cm.whatsapp || {};
    const sms = cm.sms || {};
    const call = cm.call || {};
    return (email.sent || 0) + (whatsapp.sent || 0) + (sms.sent || 0) + (call.initiated || 0);
  };

  // Get channel percentage
  const getChannelPercentage = (count) => {
    const total = getChannelTotal();
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString || (new Date(dateString)).toString() === 'Invalid Date') {
      return { date: 'TBD', time: '' };
    }
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
  };

  // Get channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return Mail;
      case 'whatsapp':
        return MessageCircle;
      case 'call':
        return Phone;
      default:
        return Mail;
    }
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    alert('Export feature coming soon');
  };

  // Handle create similar campaign
  const handleCreateSimilar = () => {
    // Navigate to campaign creation with pre-filled data
    navigate('/campaigns/autonomous/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign results...</p>
        </div>
      </div>
    );
  }

  const channelTotal = getChannelTotal();
  const goalAchievement = analytics.goal_achievement || { goal: 10, achieved: 10, percentage: 100 };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/progress`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Campaign Results</h1>
                <p className="text-sm text-gray-600 mt-1">Analytics and performance metrics</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                loadError ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {loadError ? 'Limited data' : (campaignStatus === 'completed' ? 'Completed' : 'Live')}
              </span>
              <button
                onClick={handleExport}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => navigate('/campaigns/autonomous/new')}
                className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Campaign
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loadError && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {loadError}
          </div>
        )}
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Goal Achievement */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Goal</span>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{goalAchievement.goal}</div>
            <div className="text-xs font-medium text-green-600">
              ✅ {goalAchievement.percentage}% Achieved
            </div>
          </div>

          {/* Sourced */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">Sourced</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {analytics.conversion_funnel?.sourced || analytics.progress?.sourced_count || 0}
            </div>
            <div className="text-xs text-gray-500">Candidates found</div>
          </div>

          {/* Contacted */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">Contacted</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {analytics.conversion_funnel?.contacted || analytics.progress?.contacted_count || 0}
            </div>
            <div className="text-xs text-gray-500">Candidates reached</div>
          </div>

          {/* Scheduled */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Scheduled</span>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-orange-600 mb-1">
              {analytics.conversion_funnel?.scheduled || analytics.progress?.scheduled_count || 0}
            </div>
            <div className="text-xs font-medium text-green-600">
              ✅ {goalAchievement.percentage}% Success
            </div>
          </div>
        </div>

        {/* Campaign Performance */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-500" />
            Campaign Performance
          </h2>

          {/* Conversion Funnel */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Conversion Funnel</h3>
            <div className="flex items-center justify-between gap-4 overflow-x-auto pb-4">
              <div className="flex flex-col items-center min-w-[100px]">
                <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-blue-700">
                    {analytics.conversion_funnel?.sourced || 0}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-700">Sourced</span>
              </div>

              <div className="flex-1 h-0.5 bg-gray-300 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-green-500"></div>
              </div>

              <div className="flex flex-col items-center min-w-[100px]">
                <div className="w-20 h-20 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-green-700">
                    {analytics.conversion_funnel?.evaluated || 0}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-700">Evaluated</span>
              </div>

              <div className="flex-1 h-0.5 bg-gray-300 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-orange-500"></div>
              </div>

              <div className="flex flex-col items-center min-w-[100px]">
                <div className="w-20 h-20 bg-orange-100 rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-orange-700">
                    {analytics.conversion_funnel?.contacted || 0}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-700">Contacted</span>
              </div>

              <div className="flex-1 h-0.5 bg-gray-300 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-purple-500"></div>
              </div>

              <div className="flex flex-col items-center min-w-[100px]">
                <div className="w-20 h-20 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-purple-700">
                    {analytics.conversion_funnel?.scheduled || 0}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-700">Scheduled</span>
              </div>
            </div>
          </div>

          {/* Channel Breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Channel Breakdown</h3>
            <div className="space-y-4">
              {/* Email */}
              {analytics.channel_metrics?.email?.sent > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Email</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.channel_metrics.email.sent} ({getChannelPercentage(analytics.channel_metrics.email.sent)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getChannelPercentage(analytics.channel_metrics.email.sent)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* WhatsApp */}
              {analytics.channel_metrics?.whatsapp?.sent > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.channel_metrics.whatsapp.sent} ({getChannelPercentage(analytics.channel_metrics.whatsapp.sent)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getChannelPercentage(analytics.channel_metrics.whatsapp.sent)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Call */}
              {analytics.channel_metrics?.call?.initiated > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Call</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.channel_metrics.call.initiated} ({getChannelPercentage(analytics.channel_metrics.call.initiated)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getChannelPercentage(analytics.channel_metrics.call.initiated)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* SMS */}
              {analytics.channel_metrics?.sms?.sent > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">SMS</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.channel_metrics.sms.sent} ({getChannelPercentage(analytics.channel_metrics.sms.sent)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getChannelPercentage(analytics.channel_metrics.sms.sent)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Issues / Failures section - shown when phase failures or escalations exist */}
        {(analytics.phase_failures?.length > 0 || analytics.escalations?.length > 0) && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-amber-200 bg-amber-50/30">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Issues &amp; Failures
            </h2>
            {analytics.phase_failures?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Phase Failures</h3>
                <ul className="space-y-2">
                  {analytics.phase_failures.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-amber-200 text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-gray-800 capitalize">{f.phase || 'Unknown'}</span>
                        <span className="text-gray-500 ml-2 text-xs">{f.timestamp ? new Date(f.timestamp).toLocaleString() : ''}</span>
                        <p className="text-gray-600 mt-1">{f.error || 'No details'}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {analytics.escalations?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Escalations</h3>
                <ul className="space-y-2">
                  {analytics.escalations.map((e, i) => (
                    <li key={i} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-amber-200 text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-gray-800">{e.type || e.reason || 'Escalation'}</span>
                        <p className="text-gray-600 mt-1">{e.message || e.description || JSON.stringify(e)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Timeline & Scheduled Interviews */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Timeline Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Timeline
            </h2>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-center text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">Progress over time chart</p>
                <p className="text-xs text-gray-400 mt-1">Coming soon</p>
              </div>
            </div>
          </div>

          {/* Scheduled Interviews */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-500" />
                Scheduled Interviews
              </h2>
              {scheduledInterviews.length > 5 && (
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                  View All {scheduledInterviews.length}...
                </button>
              )}
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {scheduledInterviews.length > 0 ? (
                scheduledInterviews.slice(0, 5).map((interview) => {
                  const Icon = getChannelIcon(interview.channel);
                  const { date, time } = formatDateTime(interview.datetime);
                  const dateTimeDisplay = time ? `${date} • ${time}` : date;
                  return (
                    <div key={interview.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{interview.name || 'Unknown'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-600">{dateTimeDisplay}</span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        interview.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {interview.status}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No scheduled interviews</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/progress`)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Progress
          </button>

          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/candidates`)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              View Candidates
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Report
            </button>
            <button
              onClick={handleCreateSimilar}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Create Similar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignResults;


